import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'

import useLocalStorage from '@hooks/useLocalStorage'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import {
  DEFAULT_PLANNING_LIMIT,
  PLANNING_LIMIT_OPTIONS,
  initialState,
  searchProjecten,
} from '../services/searchProjectenPlanning'

export { useProjectenPlanningSearch, queryKey }

const queryKey = 'hoofdleiding-projecten-planning'

const initialStorable = {
  ...initialState.query.filters,
  limit: DEFAULT_PLANNING_LIMIT,
}

const toValidLimit = (limit: number) =>
  PLANNING_LIMIT_OPTIONS.includes(limit) ? limit : DEFAULT_PLANNING_LIMIT

const useProjectenPlanningSearch = () => {
  const {
    item: { limit: storedLimit, ...storedFilters },
  } = useLocalStorage(queryKey, initialStorable, initialStorable)
  const [filters, setFilters] = useState(storedFilters)
  const [limit, setLimit] = useState(() => toValidLimit(storedLimit))
  const { hasPermissions } = useAuthenticatedUser()
  const storable = useMemo(() => ({ ...filters, limit }), [filters, limit])

  const onLoadStorable = useCallback((item: typeof initialStorable) => {
    const { limit: loadedLimit, ...loadedFilters } = {
      ...initialStorable,
      ...item,
    }

    setFilters(loadedFilters)
    setLimit(toValidLimit(loadedLimit))
  }, [])

  const query = { ...initialState.query, filters, page: { skip: 0, limit } }

  const { data, refetch, isFetching, isError } = useQuery({
    queryKey: [queryKey, query],
    queryFn: () => searchProjecten({ query }),
    enabled: hasPermissions,
    refetchOnMount: true,
  })

  return {
    data: data || initialState.data,
    isLoading: isFetching,
    isError,
    filters,
    onChangeFilters: setFilters,
    limit,
    onChangeLimit: setLimit,
    storable,
    onLoadStorable,
    onRefetch: refetch,
  }
}
